import React, { useState, useMemo, useCallback } from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { StringParam, useQueryParams, ArrayParam } from 'use-query-params';
import { encodeDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { ProfileAvatar } from '../../../../common/ProfileAvatar';
import { VolunteerProfile } from '../../../../common/AvatarName';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import '@fullcalendar/resource-timeline/main.css';
import '../../../../theme/src/calendar.css';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { CommonScreenSkeleton } from '../../../../common/CommonScreenSkeleton';
import { CalendarRosterDialog } from './CalendarRosterDialog';
import { CalendarRosterTutorialDialog } from './CalendarRosterTutorialDialog';
import { AddRosterDialog } from './AddRosterDialog';
import { RosterToolbar } from '../single-roster/RosterToolbar';
import { CalendarRosterFilter } from './CalendarRosterFilter';
import { RosterSettingDialog } from '../settings/RosterSettingDialog';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { getMenuLabel } from '../../../../global/auth/use-menu-labels/use-menu-labels';
import { useUser } from '../../../../global/auth/useUser';

interface CalendarActivity {
  activityId: string;
  name: string;
  programId: string;
  programName: string;
  isRecurring: boolean;
  dates: CalendarDate[];
}

interface CalendarDate {
  activityDate: string;
  hasPublishedRoster: boolean;
  rosterStatus: 'not_published' | 'action_required' | 'published';
  activityStatus: string;
  isFull: boolean;
  sessions: CalendarSession[];
  rosteredVolunteers: Array<{ volunteerId: string; name: string; avatarUrl: string | null }>;
}

interface CalendarSession {
  sessionId: string;
  name: string;
  startTime?: string;
  endTime?: string;
  minVolunteers: number;
  rosteredVolunteers: number;
  isUnderstaffed: boolean;
}

interface CalendarActivitiesResponse {
  activities: CalendarActivity[];
  dateFrom: string;
  dateUntil: string;
  totalActivities: number;
  generatedAt: string;
}

export const CalendarRosterScreen = () => {
  const { theme, isMobile, isSm } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { user } = useUser();
  const assignedRosteringViewEnabled = user.configItems.vm.assignedRosteringView === true;
  const savedCalendarViewType = user.userPreference?.calendarViewType;
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openSetting, setOpenSetting] = useState(false);
  const [showAddRosterDialog, setShowAddRosterDialog] = useState(false);
  const [dialogState, setDialogState] = useState<{
    activityId: string;
    activityDate: string;
    activityName: string;
    programName?: string;
    rosterStatus?: string;
    sessions?: any[];
    shortfall?: number;
    rosteredVolunteers?: any[];
    sessionCount?: number;
  } | null>(null);

  const [query, setQuery] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam,
    programId: StringParam,
    rosterStatuses: ArrayParam,
    statusFilters: ArrayParam,
    volunteersNeeded: StringParam,
  });

  // Legend filtering state
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>(query.statusFilters || []);

  // Handle legend item clicks
  const handleLegendClick = useCallback(
    (statusKey: string) => {
      const newFilters = selectedStatusFilters.includes(statusKey)
        ? selectedStatusFilters.filter((f) => f !== statusKey)
        : [...selectedStatusFilters, statusKey];

      setSelectedStatusFilters(newFilters);
      setQuery({ statusFilters: newFilters.length > 0 ? newFilters : undefined });
    },
    [selectedStatusFilters, setQuery]
  );

  // Default date range (following IndividualRosterScreen pattern)
  const defaultStartDate = React.useRef(encodeDate(DateTime.local()));
  const defaultEndDate = React.useMemo(() => {
    return encodeDate(DateTime.local().plus({ days: 30 }));
  }, []);

  // Initialize default dates
  React.useEffect(() => {
    if (!query.startDate) {
      setQuery({ startDate: defaultStartDate.current });
    }
    if (!query.endDate) {
      setQuery({ endDate: defaultEndDate });
    }
  }, [query.startDate, query.endDate, defaultEndDate, setQuery]);

  // Manual state for activities
  const [activities, setActivities] = React.useState<CalendarActivity[]>([]);
  const [volunteers, setVolunteers] = React.useState<any[]>([]);
  const isVolunteerViewRef = React.useRef(false);

  // Fetch with non-deferred approach - fetch on specific date range changes
  const fetchHook = useCampfireFetch<CalendarActivitiesResponse>({ defer: true });
  const { response, isLoading } = fetchHook;
  const runRef = React.useRef(fetchHook.run);
  runRef.current = fetchHook.run;

  // Update activities or volunteers when response comes in
  React.useEffect(() => {
    if (response?.data?.data?.activities) {
      setActivities(response.data.data.activities);
    } else if (response?.data?.activities) {
      setActivities(response.data.activities);
    } else if (response?.data?.data?.volunteers) {
      setVolunteers(response.data.data.volunteers);
    } else if (response?.data?.volunteers) {
      setVolunteers(response.data.volunteers);
    }
  }, [response]);

  const fetchCalendarData = React.useCallback(
    (dateInfo: any) => {
      if (!dateInfo?.start || !dateInfo?.end) {
        return;
      }

      // Use local date formatting to avoid timezone conversion issues
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Add buffer day before start to handle edge cases
      const startDate = new Date(dateInfo.start);
      startDate.setDate(startDate.getDate() - 1);
      const dateFrom = formatLocalDate(startDate);

      // FullCalendar's end date is exclusive, so it's already one day ahead
      // No need to subtract - just use it as-is for inclusive range
      const dateUntil = formatLocalDate(dateInfo.end);

      // Prepare request data
      const requestData: any = {
        dateFrom,
        dateUntil,
      };

      // Add filters if they exist
      if (query.programId) {
        requestData.programIds = [query.programId];
      }

      const isVolunteerView = dateInfo.view?.type === 'resourceTimelineMonth';
      isVolunteerViewRef.current = isVolunteerView;

      if (isVolunteerView) {
        runRef.current({
          url: `${apiUrl}/vm/activity/calendar/volunteers`,
          method: 'POST',
          data: requestData,
        });
      } else {
        if (query.rosterStatuses && query.rosterStatuses.length > 0) {
          requestData.rosterStatuses = query.rosterStatuses;
        }

        runRef.current({
          url: `${apiUrl}/vm/activity/calendar/activities`,
          method: 'POST',
          data: requestData,
        });
      }
    },
    [apiUrl, query.programId, query.rosterStatuses]
  );

  // Trigger initial fetch after component mounts
  const calendarRef = React.useRef<any>(null);
  const hasInitialFetch = React.useRef(false);
  React.useEffect(() => {
    // Only fetch once on initial mount
    if (hasInitialFetch.current) {
      return undefined;
    }

    // Small delay to ensure FullCalendar is ready
    const timer = setTimeout(() => {
      if (calendarRef.current) {
        hasInitialFetch.current = true;
        const calendarApi = calendarRef.current.getApi();
        const { currentStart, currentEnd } = calendarApi.view;
        fetchCalendarData({
          start: currentStart,
          end: currentEnd,
          view: calendarApi.view,
        });
      }
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  // Transform activities to FullCalendar events (following ManagementCalendar pattern)
  const eventArray = useMemo(() => {
    // Filter activities by search query
    const filteredActivities = searchQuery
      ? activities.filter((activity) => activity.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : activities;

    let events = filteredActivities.flatMap((activity) =>
      activity.dates.map((date) => {
        // Calculate session times
        const sessionTimes = date.sessions.filter((s) => s.startTime && s.endTime);
        const hasTimedSessions = sessionTimes.length > 0;

        // Calculate volunteer shortfall
        const totalVolunteersNeeded = date.sessions.reduce((sum, session) => sum + session.minVolunteers, 0);
        const totalVolunteersRostered = date.sessions.reduce((sum, session) => sum + session.rosteredVolunteers, 0);
        const shortfall = Math.max(0, totalVolunteersNeeded - totalVolunteersRostered);

        // Check if this roster is in the past
        const rosterDate = DateTime.fromFormat(date.activityDate, 'yyyy-MM-dd');
        const today = DateTime.local().startOf('day');
        const isPast = rosterDate < today;

        // Color palette based on roster status and whether it's past
        const getStatusColors = () => {
          // Check if activity is cancelled (regardless of past/future)
          if (date.activityStatus === 'cancelled') {
            return {
              backgroundColor: '#F3E8FF', // Purple extraLight - matches theme pattern
              textColor: '#9333EA', // Purple medium - matches theme pattern
              borderColor: '#9333EA',
            };
          }

          // Past rosters use activityStatus for proper colors (matching roster cards)
          if (isPast) {
            switch (date.activityStatus) {
              case 'not-published':
                return {
                  backgroundColor: '#EFF6FF', // Info blue - matches theme.alert.blue.extraLight
                  textColor: '#1D4ED8', // matches theme.alert.blue.medium
                };
              case 'report-not-started':
                return {
                  backgroundColor: '#FEF2F2', // Error red - matches theme.alert.red.extraLight
                  textColor: '#B91C1C', // matches theme.alert.red.medium
                  borderColor: '#B91C1C',
                };
              case 'report-missing':
                return {
                  backgroundColor: '#FFFBEB', // Warning yellow - matches theme.alert.yellow.extraLight
                  textColor: '#B45309', // matches theme.alert.yellow.medium
                  borderColor: '#B45309',
                };
              case 'completed':
                return {
                  backgroundColor: '#ECFDF5', // Success green - matches theme.alert.green.extraLight
                  textColor: '#047857', // matches theme.alert.green.medium
                  borderColor: '#047857',
                };
              default:
                return {
                  backgroundColor: '#F5F5F5',
                  textColor: '#616161',
                  borderColor: '#9E9E9E',
                };
            }
          }

          // Future rosters use alert colors
          switch (date.rosterStatus) {
            case 'not_published':
              return {
                backgroundColor: '#F5F5F5', // Grey for not published
                textColor: '#616161', // Dark grey text
                borderColor: '#9E9E9E',
              };
            case 'action_required':
              return {
                backgroundColor: '#FFFBEB', // Warning alert extraLight
                textColor: '#B45309', // Warning alert medium
                borderColor: '#B45309',
              };
            case 'published':
              return {
                backgroundColor: '#ECFDF5', // Success alert extraLight
                textColor: '#047857', // Success alert medium
                borderColor: '#047857',
              };
            default:
              return {
                backgroundColor: '#F5F5F5',
                textColor: '#616161',
                borderColor: '#9E9E9E',
              };
          }
        };

        const colors = getStatusColors();

        // Create event object
        const event: any = {
          id: `${activity.activityId}-${date.activityDate}`,
          title: activity.name,
          display: 'block',
          // Use backgroundColor and textColor directly (without 'color' prop which might conflict)
          backgroundColor: colors.backgroundColor,
          textColor: colors.textColor,
          borderColor: 'transparent',
          extendedProps: {
            activityId: activity.activityId,
            activityDate: date.activityDate,
            activityName: activity.name,
            programName: activity.programName,
            rosterStatus: date.rosterStatus,
            activityStatus: date.activityStatus,
            isPast,
            isFull: date.isFull,
            sessions: date.sessions,
            shortfall,
            rosteredVolunteers: date.rosteredVolunteers,
            sessionCount: date.sessions.length,
            isRecurring: activity.isRecurring,
            statusColor: colors.backgroundColor, // Store for list view
          },
        };

        // If no timed sessions, make it an all-day event
        if (!hasTimedSessions) {
          event.allDay = true;
          event.start = date.activityDate;
        } else {
          // Calculate earliest start and latest end time
          const startTime = sessionTimes.reduce(
            (earliest, session) => (session.startTime! < earliest ? session.startTime! : earliest),
            sessionTimes[0].startTime!
          );
          const endTime = sessionTimes.reduce(
            (latest, session) => (session.endTime! > latest ? session.endTime! : latest),
            sessionTimes[0].endTime!
          );
          // Format as ISO 8601 datetime string for FullCalendar
          event.start = `${date.activityDate}T${startTime}`;
          event.end = `${date.activityDate}T${endTime}`;
        }

        return event;
      })
    );

    // Filter events by selected status filters
    if (selectedStatusFilters.length > 0) {
      events = events.filter((event) => {
        const { activityStatus, rosterStatus, isPast } = event.extendedProps;
        return selectedStatusFilters.some((filter) => {
          if (filter === 'cancelled') return activityStatus === 'cancelled';
          if (filter === 'not-published')
            return isPast ? activityStatus === 'not-published' : rosterStatus === 'not_published';
          if (filter === 'report-not-started') return activityStatus === 'report-not-started';
          if (filter === 'report-missing') return activityStatus === 'report-missing';
          if (filter === 'completed') return activityStatus === 'completed';
          if (filter === 'action-required') return rosterStatus === 'action_required';
          if (filter === 'published') return rosterStatus === 'published';
          return false;
        });
      });
    }

    // Filter by volunteers needed
    if (query.volunteersNeeded === 'true') {
      events = events.filter((event) => {
        const { shortfall } = event.extendedProps;
        return shortfall > 0;
      });
    }

    return events;
  }, [activities, theme, searchQuery, selectedStatusFilters, query.volunteersNeeded]);

  // Transform volunteers to FullCalendar resources and events for timeline view
  const { resourcesArray, volunteerEvents } = useMemo(() => {
    // Filter volunteers by search query
    const filteredVolunteers = searchQuery
      ? volunteers.filter((volunteer) => {
          const fullName = `${volunteer.preferredName} ${volunteer.lastName}`.toLowerCase();
          return fullName.includes(searchQuery.toLowerCase());
        })
      : volunteers;

    // Sort volunteers alphabetically by name
    const sortedVolunteers = [...filteredVolunteers].sort((a, b) => {
      const nameA = `${a.preferredName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.preferredName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const resources = sortedVolunteers.map((volunteer) => ({
      id: volunteer.volunteerId,
      title: `${volunteer.preferredName} ${volunteer.lastName}`,
      extendedProps: {
        avatarUrl: volunteer.avatarUrl,
      },
    }));

    const events = sortedVolunteers.flatMap((volunteer) =>
      volunteer.rosterAssignments.map((assignment: any) => {
        let start: string;
        let end: string;

        const endDate = assignment.activityEndDate || assignment.activityDate;

        if (assignment.startTime && assignment.endTime) {
          start = `${assignment.activityDate}T${assignment.startTime}`;
          end = `${endDate}T${assignment.endTime}`;
        } else {
          start = assignment.activityDate;
          const nextDay = DateTime.fromISO(endDate)
            .plus({ days: 1 })
            .toFormat('yyyy-MM-dd');
          end = nextDay;
        }

        const getEventColor = () => {
          if (assignment.rosterStatus === 'cancelled') {
            return { bg: '#F3E8FF', text: '#9333EA', border: '#9333EA' };
          }
          if (assignment.rosterStatus === 'completed') {
            return { bg: '#ECFDF5', text: '#047857', border: '#047857' };
          }
          if (assignment.rosterStatus === 'action_required') {
            return { bg: '#FFFBEB', text: '#B45309', border: '#B45309' };
          }
          return { bg: '#ECFDF5', text: '#047857', border: '#047857' };
        };

        const colors = getEventColor();

        return {
          id: `${volunteer.volunteerId}-${assignment.activityId}-${assignment.activityDate}`,
          resourceId: volunteer.volunteerId,
          title: assignment.activityName,
          start,
          end,
          allDay: !assignment.startTime,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          textColor: colors.text,
          extendedProps: {
            activityId: assignment.activityId,
            activityDate: assignment.activityDate,
            activityName: assignment.activityName,
            programName: assignment.programName,
            rosterStatus: assignment.rosterStatus,
          },
        };
      })
    );

    return { resourcesArray: resources, volunteerEvents: events };
  }, [volunteers, searchQuery]);

  // Handle event clicks
  const handleEventClick = useCallback((eventInfo: any) => {
    eventInfo.jsEvent.preventDefault();

    const {
      activityId,
      activityDate,
      activityName,
      programName,
      rosterStatus,
      sessions,
      shortfall,
      rosteredVolunteers,
      sessionCount,
    } = eventInfo.event.extendedProps;

    // For multi-day events in volunteer timeline view, calculate which date was clicked
    let clickedDate = activityDate;
    if (isVolunteerViewRef.current && eventInfo.event.start && eventInfo.event.end) {
      const startDate = DateTime.fromJSDate(eventInfo.event.start);
      const endDate = DateTime.fromJSDate(eventInfo.event.end);
      const daysDiff = endDate.diff(startDate, 'days').days;

      // If it's a multi-day event, try to determine which day was clicked
      if (daysDiff > 1) {
        const clickX = eventInfo.jsEvent.clientX;
        const eventEl = eventInfo.el;
        const rect = eventEl.getBoundingClientRect();
        const clickOffset = clickX - rect.left;
        const percentageClicked = clickOffset / rect.width;
        const daysFromStart = Math.floor(percentageClicked * daysDiff);
        clickedDate = startDate.plus({ days: daysFromStart }).toFormat('yyyy-MM-dd');
      }
    }

    setDialogState({
      activityId,
      activityDate: clickedDate,
      activityName,
      programName,
      rosterStatus,
      sessions,
      shortfall,
      rosteredVolunteers,
      sessionCount,
    });
  }, []);

  // Custom event rendering (following ManagementCalendar patterns)
  const renderEventContent = useCallback((eventInfo: any) => {
    const { view } = eventInfo;
    const props = eventInfo.event.extendedProps;
    const rosteredVolunteers = props.rosteredVolunteers || [];

    if (view.type === 'dayGridMonth') {
      return (
        <Box overflow='hidden' textOverflow='ellipsis' padding='6px 8px'>
          <Box component='div' fontSize='12px' fontWeight='600' mb={0.5} noWrap>
            {eventInfo.event.title}
            {props.activityStatus === 'cancelled' && (
              <Box component='span' fontSize='10px' fontWeight='bold' ml={1} color={eventInfo.event.textColor}>
                CANCELLED
              </Box>
            )}
          </Box>
          <Box component='div' fontSize='10px' color='rgba(0,0,0,0.6)' mb={0.5}>
            {props.programName} • {props.sessionCount} session{props.sessionCount !== 1 ? 's' : ''}
          </Box>
          {rosteredVolunteers.length > 0 && (
            <Box display='flex' alignItems='center' gap='4px'>
              <Box display='flex'>
                {rosteredVolunteers.slice(0, 3).map((vol: any, index: number) => (
                  <Tooltip key={vol.volunteerId} title={vol.name || 'Volunteer'}>
                    <Box ml={index > 0 ? '-4px' : 0}>
                      <ProfileAvatar
                        profile={{
                          preferredName: vol.name?.split(' ')[0] || 'V',
                          lastName:
                            vol.name
                              ?.split(' ')
                              .slice(1)
                              .join(' ') || '',
                          avatarUrl: vol.avatarUrl,
                        }}
                        size='extrasmall'
                        style={{ border: '1px solid white' }}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
              <Box component='span' fontSize='10px' color={eventInfo.event.textColor}>
                {rosteredVolunteers.length} rostered
              </Box>
            </Box>
          )}
          {props.shortfall > 0 && (
            <Box component='div' fontSize='11px' mt={0.5} fontWeight='600' color={eventInfo.event.textColor}>
              Need {props.shortfall} more
            </Box>
          )}
        </Box>
      );
    }

    if (view.type === 'timeGridWeek') {
      return (
        <Box overflow='hidden' textOverflow='ellipsis' padding='8px'>
          <Box display='flex' alignItems='center' gap='6px' mb={0.5}>
            <Box component='div' fontSize='13px' fontWeight='bold' flex={1}>
              {eventInfo.event.title}
            </Box>
            {props.activityStatus === 'cancelled' && (
              <Box
                component='span'
                fontSize='10px'
                fontWeight='700'
                color='white'
                bgcolor={eventInfo.event.textColor}
                padding='2px 6px'
                borderRadius='3px'
              >
                CANCELLED
              </Box>
            )}
            {props.isFull && props.activityStatus !== 'cancelled' && (
              <Box
                component='span'
                fontSize='10px'
                fontWeight='700'
                color='white'
                bgcolor={eventInfo.event.textColor}
                padding='2px 6px'
                borderRadius='3px'
              >
                FULL
              </Box>
            )}
          </Box>
          <Box component='div' fontSize='11px' color='rgba(0,0,0,0.6)' mb={0.75}>
            {props.programName} • {props.sessionCount} session{props.sessionCount !== 1 ? 's' : ''}
          </Box>
          {rosteredVolunteers.length > 0 && (
            <Box display='flex' alignItems='center' gap='4px' mb={0.5}>
              <Box display='flex' mr='4px'>
                {rosteredVolunteers.slice(0, 5).map((vol: any, index: number) => (
                  <Tooltip key={vol.volunteerId} title={vol.name || 'Volunteer'}>
                    <Box ml={index > 0 ? '-6px' : 0}>
                      <ProfileAvatar
                        profile={{
                          preferredName: vol.name?.split(' ')[0] || 'V',
                          lastName:
                            vol.name
                              ?.split(' ')
                              .slice(1)
                              .join(' ') || '',
                          avatarUrl: vol.avatarUrl,
                        }}
                        size='small'
                        style={{ border: '1px solid white' }}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
              <Box component='span' fontSize='11px' color={eventInfo.event.textColor}>
                {rosteredVolunteers.length} rostered
              </Box>
            </Box>
          )}
          {props.shortfall > 0 && (
            <Box component='div' fontSize='12px' mt={0.5} fontWeight='600' color={eventInfo.event.textColor}>
              Need {props.shortfall} more
            </Box>
          )}
        </Box>
      );
    }

    if (view.type === 'resourceTimelineMonth' || view.type === 'resourceTimeline') {
      return (
        <Box overflow='hidden' textOverflow='ellipsis' padding='4px 8px'>
          <Box component='div' fontSize='12px' fontWeight='600'>
            {eventInfo.event.title}
          </Box>
          <Box component='div' fontSize='10px' color='rgba(0,0,0,0.6)' mt={0.5}>
            {props.programName}
          </Box>
        </Box>
      );
    }

    // Day view
    return (
      <Box overflow='hidden' textOverflow='ellipsis' padding='10px'>
        <Box display='flex' alignItems='center' gap='8px' mb={0.5}>
          <Box component='div' fontSize='14px' fontWeight='bold' flex={1}>
            {eventInfo.event.title}
          </Box>
          {props.isFull && (
            <Box
              component='span'
              fontSize='11px'
              fontWeight='700'
              color='white'
              bgcolor={eventInfo.event.textColor}
              padding='3px 8px'
              borderRadius='4px'
            >
              FULL
            </Box>
          )}
        </Box>
        <Box component='div' fontSize='12px' color='rgba(0,0,0,0.7)' mb={1.25}>
          {props.programName} • {props.sessionCount} session{props.sessionCount !== 1 ? 's' : ''}
        </Box>
        {rosteredVolunteers.length > 0 && (
          <Box display='flex' alignItems='center' gap='8px' mb={0.5}>
            <Box display='flex'>
              {rosteredVolunteers.slice(0, 8).map((vol: any, index: number) => (
                <Tooltip key={vol.volunteerId} title={vol.name || 'Volunteer'}>
                  <Box ml={index > 0 ? '-8px' : 0}>
                    <ProfileAvatar
                      profile={{
                        preferredName: vol.name?.split(' ')[0] || 'V',
                        lastName:
                          vol.name
                            ?.split(' ')
                            .slice(1)
                            .join(' ') || '',
                        avatarUrl: vol.avatarUrl,
                      }}
                      size='medium'
                      style={{ border: '2px solid white' }}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
            <Box component='span' fontSize='12px' fontWeight='500' color={eventInfo.event.textColor}>
              {rosteredVolunteers.length} rostered
            </Box>
          </Box>
        )}
        {props.shortfall > 0 && (
          <Box component='div' fontSize='13px' mt={0.5} fontWeight='600' color={eventInfo.event.textColor}>
            Need {props.shortfall} more volunteer{props.shortfall !== 1 ? 's' : ''}
          </Box>
        )}
      </Box>
    );
  }, []);

  // Toolbar handlers
  const handleToggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Check if a roster is completed (past/read-only)
  const isRosterCompleted = useCallback((activityDate: string) => {
    const rosterDate = DateTime.fromFormat(activityDate, 'yyyy-MM-dd');
    const today = DateTime.local().startOf('day');
    return rosterDate < today;
  }, []);

  const handleViewChange = useCallback(
    (view: 'single' | 'calendar' | 'bulk') => {
      if (view === 'bulk') {
        setQuery({ view: 'bulk' });
      } else if (view === 'single') {
        setQuery({ view: undefined }); // Remove view parameter for single view
      }
      // Calendar view stays on current page
    },
    [setQuery]
  );

  // Get custom label for rosters
  const org = useOrgInfo();
  const rostersLabel = getMenuLabel('managementRosters', org?.menuLabels);

  if (isLoading && !activities.length) {
    return <CommonScreenSkeleton />;
  }

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Box borderBottom='1px solid' borderColor={theme.color.grey.neutralBrand200}>
        <RosterToolbar
          title='Rosters'
          query={searchQuery}
          setQueryValue={handleSearchChange}
          isFiltering={showFilters}
          setFiltering={handleToggleFilters}
          isHistory={false}
          setHistory={() => {}}
          onSettings={() => setOpenSetting(true)}
          onCancel={() => {}}
          onViewChange={handleViewChange}
          currentView='calendar'
          onTemplate={() => {}}
          showCancel={false}
          showTemplate={false}
          originalLabel={rostersLabel.label}
          customLabel={rostersLabel.customLabel}
        />
      </Box>
      <Box flexGrow={1} display='flex' flexDirection='column' style={{ overflow: 'auto', minHeight: 0 }}>
        {showFilters && (
          <Box padding='0 16px'>
            <CalendarRosterFilter showFilters={showFilters} onCollapseFilters={() => setShowFilters(false)} />
          </Box>
        )}
        <Box flexGrow={1} display='flex' flexDirection='column' style={{ minHeight: 0 }}>
          <Box flexGrow={1} padding='24px 16px 0' position='relative' style={{ minHeight: 0 }}>
            <style>
              {`
                .fc-toolbar-title {
                  font-size: 18px !important;
                }
                .fc-timeline-lane-frame .MuiAvatar-root {
                  width: 30px !important;
                  height: 30px !important;
                  font-size: 12px !important;
                }
                .fc-datagrid-cell-frame {
                  display: flex !important;
                  align-items: center !important;
                }
              `}
            </style>
            <FullCalendar
              ref={calendarRef}
              // Remove dynamic key to prevent re-initialization on filter changes
              initialView={isMobile ? 'dayGridMonth' : savedCalendarViewType || 'timeGridWeek'}
              plugins={[timeGridPlugin, dayGridPlugin, listPlugin, interactionPlugin, resourceTimelinePlugin]}
              allDaySlot
              nowIndicator
              height='100%'
              customButtons={{
                addRoster: {
                  text: '+ Add Roster',
                  click: () => {
                    // Open add roster dialog
                    setShowAddRosterDialog(true);
                  },
                },
                refresh: {
                  text: '↻',
                  click: () => {
                    // Refresh calendar data
                    if (calendarRef.current) {
                      const calendarApi = calendarRef.current.getApi();
                      const { currentStart, currentEnd } = calendarApi.view;
                      fetchCalendarData({ start: currentStart, end: currentEnd, view: calendarApi.view });
                    }
                  },
                },
              }}
              headerToolbar={{
                start: isMobile ? 'prev' : isSm ? 'prev,next' : 'prev,next today',
                center: 'title',
                end: isMobile
                  ? 'next'
                  : `refresh timeGridDay,timeGridWeek,dayGridMonth,listMonth${
                      assignedRosteringViewEnabled ? ',resourceTimelineMonth' : ''
                    } addRoster`,
              }}
              views={{
                timeGridDay: {
                  slotDuration: '01:00:00',
                  buttonText: 'Day',
                },
                timeGridWeek: {
                  slotDuration: '01:00:00',
                  buttonText: 'Week',
                },
                dayGridMonth: {
                  dayMaxEvents: 3,
                  moreLinkText: 'more',
                  buttonText: 'Month',
                },
                listMonth: {
                  buttonText: 'List',
                },
                ...(assignedRosteringViewEnabled && {
                  resourceTimelineMonth: {
                    type: 'resourceTimeline',
                    duration: { months: 1 },
                    buttonText: 'Assigned',
                    slotDuration: { days: 1 },
                    slotLabelFormat: {
                      weekday: 'short',
                      month: 'numeric',
                      day: 'numeric',
                      omitCommas: true,
                    },
                  },
                }),
              }}
              schedulerLicenseKey='GPL-My-Project-Is-Open-Source'
              resources={resourcesArray}
              resourceAreaHeaderContent=' '
              resourceAreaWidth='200px'
              handleWindowResize
              events={isVolunteerViewRef.current ? volunteerEvents : eventArray}
              datesSet={fetchCalendarData}
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              resourceLabelContent={(resourceInfo) => {
                const volunteer = volunteers.find((v) => v.volunteerId === resourceInfo.resource.id);
                if (!volunteer) return null;

                const fullName = `${volunteer.preferredName} ${volunteer.lastName}`;

                return (
                  <Box
                    display='flex'
                    alignItems='center'
                    padding='8px 8px'
                    fontSize='13px'
                    color='#616161'
                    height='100%'
                  >
                    <VolunteerProfile
                      profileId={volunteer.profileId}
                      userId={volunteer.userId}
                      preferredName={volunteer.preferredName}
                      lastName={volunteer.lastName}
                      name={fullName}
                      avatarUrl={volunteer.avatarUrl}
                      noTrim
                      tooltipAll={false}
                      inTable
                    />
                  </Box>
                );
              }}
              slotLabelContent={
                isVolunteerViewRef.current
                  ? (slotInfo) => {
                      const { date } = slotInfo;
                      const dayNum = date.getDate();
                      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
                      return (
                        <Box textAlign='center' padding='4px'>
                          <Box fontSize='11px' fontWeight={500} color='#757575' textTransform='uppercase'>
                            {weekday}
                          </Box>
                          <Box fontSize='13px' fontWeight={600} color='#212121' marginTop='2px'>
                            {dayNum}
                          </Box>
                        </Box>
                      );
                    }
                  : undefined
              }
              eventDidMount={(info) => {
                // Directly set background color and remove borders to ensure they persist
                const { el, event } = info;
                if (el && event.backgroundColor) {
                  const element = el as HTMLElement;
                  element.style.backgroundColor = event.backgroundColor;
                  element.style.border = 'none';
                  element.style.borderColor = 'transparent';
                  element.style.borderWidth = '0';

                  // Set background on ALL nested elements to prevent grey showing through
                  const bgEl = element.querySelector('.fc-event-bg');
                  if (bgEl) {
                    (bgEl as HTMLElement).style.backgroundColor = event.backgroundColor;
                  }

                  const mainEl = element.querySelector('.fc-event-main');
                  if (mainEl) {
                    (mainEl as HTMLElement).style.backgroundColor = event.backgroundColor;
                    (mainEl as HTMLElement).style.border = 'none';
                  }

                  // Target all inner elements
                  const allInner = element.querySelectorAll('*');
                  allInner.forEach((child) => {
                    const childElement = child as HTMLElement;
                    childElement.style.borderColor = 'transparent';
                  });
                }
              }}
              eventClassNames={(arg) => {
                // Add custom class based on roster status for list view styling
                const { rosterStatus: status, activityStatus, isPast } = arg.event.extendedProps;

                if (isPast && activityStatus) {
                  return `list-event-${activityStatus}`;
                }
                if (status) {
                  return `list-event-${status}`;
                }
                // Default to not_published if no status
                return 'list-event-not_published';
              }}
              locale='en-au'
              slotMinTime='00:00:00'
              slotMaxTime='24:00:00'
            />
          </Box>

          {/* Color Legend */}
          <Box
            padding='6px 16px 10px'
            borderTop='1px solid'
            borderColor={theme.color.grey.neutralBrand200}
            flexShrink={0}
          >
            <Box display='flex' justifyContent='center'>
              {/* Cancelled Activities */}
              <Box mr='50px'>
                <Box
                  component='div'
                  fontSize='9px'
                  color='#9E9E9E'
                  textTransform='uppercase'
                  letterSpacing='0.5px'
                  mb='6px'
                  textAlign='center'
                >
                  Cancelled
                </Box>
                <Box display='flex' justifyContent='center'>
                  <Box
                    display='flex'
                    alignItems='center'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('cancelled')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('cancelled') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('cancelled') ? '#9333EA' : '#F3E8FF'}
                      border='1px solid #9333EA'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Cancelled
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Past roster statuses */}
              <Box mr='50px'>
                <Box
                  component='div'
                  fontSize='9px'
                  color='#9E9E9E'
                  textTransform='uppercase'
                  letterSpacing='0.5px'
                  mb='6px'
                  textAlign='center'
                >
                  Past Rosters
                </Box>
                <Box display='flex'>
                  <Box
                    display='flex'
                    alignItems='center'
                    mr='20px'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('not-published')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('not-published') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('not-published') ? '#1D4ED8' : '#EFF6FF'}
                      border='1px solid #1D4ED8'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Not Published
                    </Box>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    mr='20px'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('report-not-started')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('report-not-started')
                        ? 1
                        : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('report-not-started') ? '#B91C1C' : '#FEF2F2'}
                      border='1px solid #B91C1C'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Report Not Started
                    </Box>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    mr='20px'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('report-missing')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('report-missing') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('report-missing') ? '#B45309' : '#FFFBEB'}
                      border='1px solid #B45309'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Report Missing
                    </Box>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('completed')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('completed') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('completed') ? '#047857' : '#ECFDF5'}
                      border='1px solid #047857'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Completed
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Upcoming roster statuses */}
              <Box>
                <Box
                  component='div'
                  fontSize='9px'
                  color='#9E9E9E'
                  textTransform='uppercase'
                  letterSpacing='0.5px'
                  mb='6px'
                  textAlign='center'
                >
                  Upcoming Rosters
                </Box>
                <Box display='flex'>
                  <Box
                    display='flex'
                    alignItems='center'
                    mr='20px'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('not-published')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('not-published') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('not-published') ? '#9E9E9E' : '#F5F5F5'}
                      border='1px solid #9E9E9E'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Not Published
                    </Box>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    mr='20px'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('action-required')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('action-required') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('action-required') ? '#B45309' : '#FFFBEB'}
                      border='1px solid #B45309'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Action Required
                    </Box>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleLegendClick('published')}
                    opacity={
                      selectedStatusFilters.length === 0 || selectedStatusFilters.includes('published') ? 1 : 0.4
                    }
                  >
                    <Box
                      width='12px'
                      height='12px'
                      borderRadius='50%'
                      bgcolor={selectedStatusFilters.includes('published') ? '#047857' : '#ECFDF5'}
                      border='1px solid #047857'
                      mr='8px'
                    />
                    <Box component='span' fontSize='11px' color='#616161'>
                      Published
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Roster Dialog */}
      {dialogState && (
        <CalendarRosterDialog
          activityId={dialogState.activityId}
          activityDate={dialogState.activityDate}
          activityName={dialogState.activityName}
          programName={dialogState.programName}
          rosterStatus={dialogState.rosterStatus}
          sessionCount={dialogState.sessionCount}
          isRosterCompleted={isRosterCompleted(dialogState.activityDate)}
          onClose={() => setDialogState(null)}
          onRosterUpdate={() => {
            // Refetch current view
            if (calendarRef.current) {
              const calendarApi = calendarRef.current.getApi();
              const { currentStart, currentEnd } = calendarApi.view;
              fetchCalendarData({
                start: currentStart,
                end: currentEnd,
                view: calendarApi.view,
              });
            }
          }}
        />
      )}

      {/* Tutorial Dialog */}
      <CalendarRosterTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      {/* Settings Dialog */}
      {openSetting && <RosterSettingDialog open={openSetting} onClose={() => setOpenSetting(false)} />}

      {/* Add Roster Dialog */}
      {showAddRosterDialog && (
        <AddRosterDialog
          onClose={() => setShowAddRosterDialog(false)}
          onSuccess={() => {
            // Refetch calendar data after successfully adding a roster
            if (calendarRef.current) {
              const calendarApi = calendarRef.current.getApi();
              const { currentStart, currentEnd } = calendarApi.view;
              fetchCalendarData({ start: currentStart, end: currentEnd, view: calendarApi.view });
            }
          }}
        />
      )}
    </Box>
  );
};
